import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const SESSION_KEY = "gt_session_id";

function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function getABVariant(): string | null {
  return sessionStorage.getItem("gt_ab_variant");
}

// Fire-and-forget insert
function trackEvent(eventType: string, pagePath: string, eventData: Record<string, any> = {}) {
  supabase.from("page_events").insert({
    session_id: getSessionId(),
    event_type: eventType,
    page_path: pagePath,
    event_data: eventData,
    ab_variant: getABVariant(),
  }).then(() => {});
}

export function usePageTracking() {
  const startTime = useRef(Date.now());
  const maxScroll = useRef(0);
  const tracked = useRef(false);

  useEffect(() => {
    const path = window.location.pathname + window.location.hash;
    startTime.current = Date.now();
    maxScroll.current = 0;
    tracked.current = false;

    // Track pageview
    trackEvent("pageview", path, {
      referrer: document.referrer,
      screen_width: window.innerWidth,
    });

    // Track scroll depth
    const handleScroll = () => {
      const scrollPercent = Math.round(
        ((window.scrollY + window.innerHeight) / document.documentElement.scrollHeight) * 100
      );
      if (scrollPercent > maxScroll.current) {
        maxScroll.current = scrollPercent;
      }
    };

    // Track time on page + scroll on unload
    const handleUnload = () => {
      if (tracked.current) return;
      tracked.current = true;
      const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);
      
      // Use sendBeacon for reliability on page unload
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/page_events`;
      const events = [
        {
          session_id: getSessionId(),
          event_type: "time_on_page",
          page_path: path,
          event_data: { seconds: timeOnPage },
          ab_variant: getABVariant(),
        },
        {
          session_id: getSessionId(),
          event_type: "scroll_depth",
          page_path: path,
          event_data: { max_percent: maxScroll.current },
          ab_variant: getABVariant(),
        },
      ];
      
      navigator.sendBeacon(
        url,
        new Blob([JSON.stringify(events)], { type: "application/json" })
      );
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("beforeunload", handleUnload);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") handleUnload();
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeunload", handleUnload);
      handleUnload();
    };
  }, []);
}

export function useTrackClick() {
  return useCallback((elementId: string, label?: string) => {
    trackEvent("click", window.location.pathname, {
      element_id: elementId,
      label: label || elementId,
    });
  }, []);
}

export { trackEvent };
